import React, { useMemo, useState } from "react";
import { calcPPF, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, Pills, RightPanel, Divider, HeroValue } from "./CalcUI";

const blocks = ["15 Years", "20 Years", "25 Years", "30 Years"];
const blockMap = { "15 Years": 15, "20 Years": 20, "25 Years": 25, "30 Years": 30 };

export const PPFTab = () => {
  const [annual, setAnnual] = useState(150000);
  const [rate, setRate] = useState(7.1);
  const [tenureLabel, setTenureLabel] = useState("15 Years");

  const years = blockMap[tenureLabel] || 15;
  const result = useMemo(() => calcPPF(annual, rate, years), [annual, rate, years]);
  const animKey = `ppf-${Math.round(result.maturity)}-${annual}-${rate}-${years}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <InputRow
          label="Yearly Investment"
          valueText={`Rs. ${formatINR(annual)}`}
          valueTestId="ppf-annual-value"
          finePrint={<><span>Rs.500</span><span>Rs.1,50,000 (annual cap)</span></>}
        >
          <Slider value={annual} min={500} max={150000} step={500} onChange={setAnnual} ariaLabel="PPF yearly investment" />
        </InputRow>

        <InputRow
          label="Interest Rate (Oct-Dec 2026: 7.1%)"
          valueText={`${rate.toFixed(2)}% per annum`}
          valueTestId="ppf-rate-value"
          finePrint={<><span>6%</span><span>9% per annum</span></>}
        >
          <Slider value={rate} min={6} max={9} step={0.05} onChange={setRate} ariaLabel="PPF interest rate" />
        </InputRow>

        <div className="mb-7">
          <div className="mb-3" style={{ color: "#4A5568", fontSize: 13, fontWeight: 500 }}>
            Tenure <span style={{ color: "#718096", fontWeight: 400 }}>(extendable in 5-yr blocks after 15)</span>
          </div>
          <Pills items={blocks} value={tenureLabel} onChange={setTenureLabel} prefix="ppf-block" />
        </div>

        <p style={{ color: "#718096", fontSize: 11, lineHeight: 1.5 }}>
          PPF interest is tax-free (EEE). Annual contribution cap is Rs.1,50,000. Assumes each year's deposit is
          made by 5 April and the rate stays the same; the government revises it every quarter.
        </p>
      </div>

      <RightPanel testId="ppf-results">
        <HeroValue label="Maturity Value" value={`Rs. ${formatINR(result.maturity)}`} animKey={animKey} testId="ppf-maturity-value" />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Total Invested" value={`Rs. ${formatINR(result.invested)}`} testId="ppf-row-invested" />
          <ResultRow label="Total Interest" value={`Rs. ${formatINR(result.gains)}`} valueColor="#1D9E75" testId="ppf-row-gains" />
          <ResultRow label="Total Value" value={`Rs. ${formatINR(result.maturity)}`} testId="ppf-row-total" />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.investedPct}
          rightPct={result.gainsPct}
          leftLabel="Invested"
          rightLabel="Interest"
          leftColor="#F4A700"
          rightColor="#1D9E75"
          testId="ppf-split-bar"
        />
      </RightPanel>
    </div>
  );
};
